import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, firestore, collection, where, query, getDocs, addDoc } from '../firebase.config';
import { useParams, useNavigate } from 'react-router-dom';
import { TextField, Button, Container, CircularProgress, Typography,  MenuItem, Select, InputLabel, FormControl, Box,  Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel
 } from '@mui/material';
 import { getAuth } from "firebase/auth";

function EditForm({ isAdmin }) {
  const { id } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [isEditable, setIsEditable] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMember = async () => {
      try {
        const memberRef = collection(firestore, 'MembershipMaster');
        const q = query(memberRef, where('id', '==', id)); // Query by custom 'id' field
        const querySnapshot = await getDocs(q);
  
        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0].data(); // Assuming 'id' is unique, get the first matching document
          setMember(docData);
        } else {
          console.log("No document found with the specified ID field.");
          setError('Member not found.');
        }
      } catch (error) {
        console.error("Error fetching document: ", error);
        setError('Error fetching document.');
      } finally {
        setLoading(false);
      }
    };
  
    fetchMember();
  }, [id]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setMember((prevItem) => ({
      ...prevItem,
      [name]: value,
    }));
  };


  const handleSave = async () => {
    const auth = getAuth(); // Firebase Auth instance
    const currentUser = auth.currentUser;
  
    if (!currentUser) {
      alert("No authenticated user found. Please log in.");
      return;
    }
  
    const editorEmail = currentUser.email; // Get the editor's email
    
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(member.mobileNo)) {
      alert("Mobile number must be 10 digits long.");
      return;
    }
  
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(member.professionalEmail)) {
      alert("Please enter a valid professional email.");
      return;
    }
  
    if (member.personalEmail && !emailRegex.test(member.personalEmail)) {
      alert("Please enter a valid personal email.");
      return;
    }
  
    try {
      const memberRef = collection(firestore, 'MembershipMaster');
      const q = query(memberRef, where('id', '==', id));
      const querySnapshot = await getDocs(q);
  
      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        const oldData = querySnapshot.docs[0].data(); // Fetch current data before updating
  
        // Update the member's data
        await updateDoc(docRef, member);
  
        // Log the changes
        const changes = Object.keys(member).reduce((diff, key) => {
          if (oldData[key] !== member[key]) {
            diff[key] = { old: oldData[key], new: member[key] };
          }
          return diff;
        }, {});
  
        const editLogRef = collection(firestore, 'Editmemberlog');
        await addDoc(editLogRef, {
          editedBy: editorEmail, // Store the editor's email
          editedAt: new Date(),
          memberId: id,
          changes,
        });
  
        setIsEditable(false);
        navigate(`/editMembership/${id}`);
      } else {
        console.log("No document found with the specified ID field.");
        setError('Member not found.');
      }
    } catch (error) {
      console.error("Error updating document: ", error);
      setError('Error updating document.');
    }
  };
  

  const handleEdit = () => {
    setIsEditable(true);
  };

  const handleCancel = () => {
    setIsEditable(false);
    setValidationError('');
  
    const fetchMember = async () => {
      try {
        const memberRef = collection(firestore, 'MembershipMaster');
        const q = query(memberRef, where('id', '==', id)); // Query by custom 'id' field
        const querySnapshot = await getDocs(q);
  
        if (!querySnapshot.empty) {
          setMember(querySnapshot.docs[0].data()); // Get the first matching document
        } else {
          console.log("No document found with the specified ID field.");
          setError('Member not found.');
        }
      } catch (error) {
        console.error("Error fetching document: ", error);
        setError('Error fetching document.');
      }
    };
  
    fetchMember();
  };

  const handleBack = () => {
    navigate('/membermgmt');
    setIsEditable('false')
  };

  if (loading) {
    return (
      <Container>
        <Typography variant="h6">Loading...</Typography>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography variant="h6" color="error">{error}</Typography>
      </Container>
    );
  }

  if (!member) {
    return (
      <Container>
        <Typography variant="h6">Member details not found</Typography>
      </Container>
    );
  }

  const textFieldStyle = {
    pointerEvents: !isEditable ? 'none' : 'auto',
    cursor: !isEditable ? 'default' : 'text',
    border: !isEditable ? 'none' : '1px',
    backgroundColor: !isEditable ? 'transparent' : 'transparent',
  };

  return (
    <Container>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom paddingTop={5}>View and Edit Member details</Typography>
        <div>
          {!isEditable ? (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={handleEdit}
                style={{ marginRight: '8px' }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleBack}
              >
                Back
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                style={{ marginRight: '8px' }}
              >
                Save
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
      {validationError && <Typography variant="h6" color="error">{validationError}</Typography>}
      
      <TextField
        label="Salutation"
        name="salutation"
        value={member.salutation}
        onChange={handleChange}
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="First Name"
        name="firstName"
        value={member.firstName}
        onChange={handleChange}
        fullWidth
        required
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="Surname"
        name="surname"
        value={member.surname}
        onChange={handleChange}
        fullWidth
        required
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="Mobile No"
        name="mobileNo"
        value={member.mobileNo}
        onChange={handleChange}
        inputProps={{ maxLength: 10, pattern: '[0-9]*', inputMode: 'numeric' }}
        fullWidth
        required
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="Alternate Contact No"
        name="alternateContactNo"
        value={member.alternateContactNo}
        onChange={handleChange}
        inputProps={{ maxLength: 10, pattern: '[0-9]*', inputMode: 'numeric' }}
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="Professional Email"
        name="professionalEmail"
        value={member.professionalEmail}
        onChange={handleChange}
        fullWidth
        required
        type="email"
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="Personal Email"
        name="personalEmail"
        value={member.personalEmail}
        onChange={handleChange}
        fullWidth
        required
        type="email"
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="Date of Birth"
        name="dob"
        value={member.dob}
        onChange={handleChange}
        fullWidth
        required
        type="date"
        InputLabelProps={{ shrink: true }}
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
       <TextField
        label="Membership Start Date"
        name="membershipStartDate"
        value={member.membershipStartDate}
        onChange={handleChange}
        fullWidth
        required
        type="date"
        InputLabelProps={{ shrink: true }}
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: 'true',
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
       <TextField
        label="Membership End Date"
        name="membershipEndDate"
        value={member.membershipEndDate}
        onChange={handleChange}
        fullWidth
        required
        type="date"
        InputLabelProps={{ shrink: true }}
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <FormControl fullWidth sx={{ mb: 2 }} >
        <InputLabel>Membership Category</InputLabel>
        
        <Select
          name="membershipCategory"
          value={member.membershipCategory}
          onChange={handleChange}
          required
        >
          <MenuItem value="Individual Annual">Individual Annual</MenuItem>
          <MenuItem value="Individual (5 Years)">Individual (5 Years)</MenuItem>
          <MenuItem value="Student">Student</MenuItem>
          <MenuItem value="Institutional">Institutional</MenuItem>
          <MenuItem value="Industry/Corporate">Industry/Corporate</MenuItem>
        </Select>
      </FormControl>

      {/* Add Alternative Membership ID field - only editable by admin */}
      {isAdmin && (
        <TextField
          label="Alternative Membership ID"
          name="alternativeMemberId"
          value={member.alternativeMemberId || ''}
          onChange={handleChange}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            readOnly: !isEditable,
          }}
          style={{
            backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
            cursor: !isEditable ? "not-allowed" : "text",
          }}
        />
      )}
      
      <TextField
        label="Resi Address (Line 1)"
        name="resiAddress1"
        value={member.resiAddress1}
        onChange={handleChange}
        fullWidth
        required
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="Resi Address (Line 2)"
        name="resiAddress2"
        value={member.resiAddress2}
        onChange={handleChange}
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="City"
        name="city"
        value={member.city}
        onChange={handleChange}
        fullWidth
        required
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="Pincode"
        name="pincode"
        value={member.pincode}
        onChange={handleChange}
        type="number"
        fullWidth
        required
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />

      {/* Gender Selection */}
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <FormLabel component="legend">Gender</FormLabel>
        <RadioGroup
          name="gender"
          value={member.gender}
          onChange={handleChange}
          row
        >
          <FormControlLabel
            value="Male"
            control={<Radio />}
            label="Male"
          />
          <FormControlLabel
            value="Female"
            control={<Radio />}
            label="Female"
          />
          <FormControlLabel
            value="Do not want to specify"
            control={<Radio />}
            label="Do not want to specify"
          />
        </RadioGroup>
      </FormControl>

      {/* Qualification and Organisation */}
      <TextField
        label="Qualification (Starting with Highest)"
        name="qualification"
        value={member.qualification}
        onChange={handleChange}
        fullWidth
        required
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="Organisation"
        name="organisation"
        value={member.organisation}
        onChange={handleChange}
        fullWidth
        required
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="No of Employees in Organisation"
        name="noOfEmployees"
        value={member.noOfEmployees}
        onChange={handleChange}
        type="number"
        required
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="Designation"
        name="designation"
        value={member.designation}
        onChange={handleChange}
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="Total Experience (in years)"
        name="experience"
        value={member.experience}
        onChange={handleChange}
        type="number"
        fullWidth
        required
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />

      {/* Expertise Area Dropdown */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Area of Expertise/Work</InputLabel>
        <Select
          name="expertise"
          value={member.expertise}
          onChange={handleChange}
          required
        >
          <MenuItem value="HR/Admin">HR/Admin</MenuItem>
          <MenuItem value="Finance/Accts">Finance/Accts</MenuItem>
          <MenuItem value="Marketing/Sales">Marketing/Sales</MenuItem>
          <MenuItem value="Materials/Supply Chain">Materials/Supply Chain</MenuItem>
          <MenuItem value="Operations/Manufacturing">Operations/Manufacturing</MenuItem>
          <MenuItem value="IT/ITES">IT/ITES</MenuItem>
          <MenuItem value="Agriculture">Agriculture</MenuItem>
          <MenuItem value="Hospitality">Hospitality</MenuItem>
          <MenuItem value="Healthcare">Healthcare</MenuItem>
          <MenuItem value="Travel/Tourism">Travel/Tourism</MenuItem>
          <MenuItem value="Trading/Exports">Trading/Exports</MenuItem>
          <MenuItem value="Consultancy">Consultancy</MenuItem>
          <MenuItem value="Corporate Training">Corporate Training</MenuItem>
          <MenuItem value="Higher Education/Academics">Higher Education/Academics</MenuItem>
          <MenuItem value="School Education">School Education</MenuItem>
          <MenuItem value="Social Work">Social Work</MenuItem>
          <MenuItem value="Media/Entertainment">Media/Entertainment</MenuItem>
          <MenuItem value="Other">Other</MenuItem>
        </Select>
      </FormControl>

      {/* Payment Details */}
      <TextField
        label="Payment Date"
        name="paymentDate"
        value={member.paymentDate}
        onChange={handleChange}
        fullWidth
        type="date"
        InputLabelProps={{ shrink: true }}
        sx={{ mb: 2 }}
      />
      <TextField
        label="Payment Amount"
        name="paymentAmount"
        value={member.paymentAmount}
        onChange={handleChange}
        fullWidth
        type="number"
        sx={{ mb: 2 }}
      />
      <TextField
        label="Payment Reference"
        name="paymentRef"
        value={member.paymentRef}
        onChange={handleChange}
        fullWidth
        sx={{ mb: 2 }}
      />

      {/* Purpose of Joining */}
      <TextField
        label="Purpose of Joining"
        name="purposeOfJoining"
        value={member.purposeOfJoining}
        onChange={handleChange}
        fullWidth
        multiline
        rows={2}
        sx={{ mb: 2 }}
      />

      {/* Social Media Links */}
      <TextField
        label="LinkedIn Profile"
        name="linkedIn"
        value={member.linkedIn}
        onChange={handleChange}
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="Facebook Profile"
        name="facebook"
        value={member.facebook}
        onChange={handleChange}
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
      <TextField
        label="Instagram Profile"
        name="instagram"
        value={member.instagram}
        onChange={handleChange}
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />

      {/* Referral Source Dropdown */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>How Did You Come to Know About Us?</InputLabel>
        <Select
          name="referralSource"
          value={member.referralSource}
          onChange={handleChange}
          required
        >
          <MenuItem value="WhatsApp">WhatsApp</MenuItem>
          <MenuItem value="News paper">News paper</MenuItem>
          <MenuItem value="Social Media">Social Media</MenuItem>
          <MenuItem value="Referral from existing Member">
            Referral from existing Member
          </MenuItem>
          <MenuItem value="Any other">Any other</MenuItem>
        </Select>
      </FormControl>

      {/* Additional Comments */}
      <TextField
        label="Additional Comments / Remarks"
        name="additionalComments"
        value={member.additionalComments}
        onChange={handleChange}
        fullWidth
        multiline
        rows={4}
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: !isEditable,
        }}
        style={{
          backgroundColor: !isEditable ? "#f5f5f5" : "transparent",
          cursor: !isEditable ? "not-allowed" : "text",
        }}
      />
    </Container>
  );
}

export default EditForm;
